import React, { Component } from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'
import Flickity from 'flickity'
import LightboxSlide from './lightbox-slide'
import {
  idleLightbox,
  mediaTypeLightbox
} from './actions'
import {
  getLightboxIdle,
  getLightboxMediaType
} from './selectors'
import {
  getCollectionTitle,
  getCollectionSubtitle,
  getCollectionLightboxSlides
} from '../collection-container/selectors'
import {
  getViewportHeight,
  getViewportWidth
} from '../app/selectors'
import './lightbox-container.css'

import { DISABLE_MOBILE_DRAG } from 'constants'

const mapStateToProps = state => {
  return {
    vw: getViewportWidth(state),
    vh: getViewportHeight(state),
    idle: getLightboxIdle(state),
    title: getCollectionTitle(state),
    subtitle: getCollectionSubtitle(state),
    slides: getCollectionLightboxSlides(state),
    mediaType: getLightboxMediaType(state)
  }
}

const mapDispatchToProps = dispatch => {
  return {
    onInteraction() {
      dispatch(idleLightbox(false))
    },
    onIdle() {
      dispatch(idleLightbox(true))
    },
    onMediaType(mediaType) {
      dispatch(mediaTypeLightbox(mediaType))
    }
  }
}

class Lightbox extends Component {

  constructor(props) {
    super(props)
    this.isDraggable = true
  }

  componentDidMount() {
    if (this.props.active) {
      this.initLightbox() 
    }
  }

  componentDidUpdate(prevProps) {
    const {
      active: nextActive
    } = this.props
    const {
      active: prevActive
    } = prevProps

    if (nextActive !== prevActive) {
      if (nextActive) {
        this.initLightbox()
      } else {
        this.destroyLightbox()
      }
    }

    if (!this.props.active) return
    
    if (!this.flick) {
      this.initFlickity()    
    } else {
      this.updateFlickity()
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      pageExitComplete: nextPageExitComplete
    } = nextProps
    const {
      pageExitComplete: prevPageExitComplete
    } = this.props
    if (nextPageExitComplete !== prevPageExitComplete) {
      if (nextPageExitComplete) {
        this.destroyFlickity()
      }
    }
  }

  componentWillUnmount() {
    this.destroyLightbox()
  }

  initLightbox() {
    this.startIdleTimer()
    this.addListeners()
    if (!this.flick) {
      this.initFlickity()
    }
    this.props.onMediaType(this.getCurrentMediaType())
  }

  destroyLightbox() {
    this.destroyFlickity()
    this.clearIdleTimer()
    this.removeListeners()
  }

  addListeners() {
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('keyup', this.handleKeyUp)
  }

  removeListeners() {
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('keyup', this.handleKeyUp)
  }

  handleKeyUp = e => {
    const { 
      slides,
      handleClickBack,
      handleClickNext,
      handleClickPrev } = this.props 

    const { keyCode } = e
    const escKey = 27
    const leftKey = 37
    const rightKey = 39

    if (escKey === keyCode) {
      handleClickBack()
    } 

    if (leftKey === keyCode) {
      handleClickPrev(slides && slides.length)
    }

    if (rightKey === keyCode) {
      handleClickNext(slides && slides.length)
    }
  }

  handleMouseMove = e => {
    const { idle, onInteraction } = this.props

    if (idle && e.pageY < 300) {
      onInteraction && onInteraction()

      // Restart idle timer 
      this.clearIdleTimer()
      this.startIdleTimer()
    }
  }

  handleIdleTimeout = () => {
    const { onIdle } = this.props 
    onIdle && onIdle()
  }

  startIdleTimer() {
    this.idleTimeout = setTimeout(this.handleIdleTimeout, 3000)
  }

  clearIdleTimer() {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout)
      this.idleTimeout = null
    }
  }

  destroyFlickity() {
    if (this.flick) {
      this.flick.off('cellSelect', this.updateSelected)
      this.flick.destroy()
      this.flick = null
    }
  }

  updateFlickity() {
    const { currentIndex } = this.props

    if (currentIndex) {
      const index = currentIndex - 1
      
      if (index !== this.flick.selectedIndex) {
        this.flick.select(index, false, true)
      }
    } else {
      console.log('Cannot update Flickity as current index is null')      
    }
  }

  initFlickity() {
    Flickity.prototype.hasDragStarted = function( moveVector ) {
      return !this.isTouchScrolling && Math.abs( moveVector.y ) < Math.abs( moveVector.x )
    };
    const { currentIndex, onMediaType, slides } = this.props
    const el = this.carouselEl 

    if (el && slides) {
      const opts = {
        cellSelector: '.lightbox__slide',
        contain: true,
        initialIndex: currentIndex - 1,
        accessibility: true,
        setGallerySize: false,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true
      }

      this.flick = new Flickity(el, opts)
      this.flick.on('cellSelect', this.updateSelected)

      const mediaType = this.getCurrentMediaType()

      if (DISABLE_MOBILE_DRAG && mediaType === 'video' && this.isDraggable) {
        this.flick.unbindDrag()
        this.isDraggable = false
      }

      onMediaType && onMediaType(mediaType)
    }
  }

  getCurrentMediaType() {
    const { slides, currentIndex } = this.props

    let currentSlide = null

    if (slides && slides.length) {
      currentSlide = slides[currentIndex - 1]
    }
    return currentSlide && currentSlide.type
  }

  updateSelected = () => {
    const { handleUpdateSelected, onMediaType } = this.props 
    handleUpdateSelected && handleUpdateSelected(this.flick.selectedIndex)

    const mediaType = this.getCurrentMediaType()

    if (mediaType === 'video') {
      if (DISABLE_MOBILE_DRAG && this.isDraggable) {
        this.flick.unbindDrag()
        this.isDraggable = false
      }
    } else {
      if (DISABLE_MOBILE_DRAG && !this.isDraggable) {
        this.flick.bindDrag()
        this.isDraggable = true
      }
    }

    onMediaType && onMediaType(mediaType)
  }

  renderUI() {
    const {
      currentIndex,
      idle,
      title,
      subtitle,
      slides,
      handleClickBack,
      handleClickNext,
      handleClickPrev
    } = this.props 

    return (
      <div className={classnames({'lightbox__ui--idle': idle}, 'lightbox__ui')}>
        <div className="lightbox__ui-title">
          <div className="text--small-serif">{title}</div>
          <div className="text--small text--uppercase" dangerouslySetInnerHTML={{ __html: subtitle }} />
        </div>
        <div className="lightbox__ui-nav">
          <div onClick={() => { handleClickPrev(slides && slides.length) }} className="lightbox__ui-nav-prev"></div>
          <div onClick={() => { handleClickNext(slides && slides.length) }} className="lightbox__ui-nav-next"></div>
        </div>
        <ul className="lightbox__ui-list">
          <li className="lightbox__ui-listitem lightbox__ui-item-count text--small">
            <span className="lightbox__ui-item-count-current">{currentIndex}</span> &mdash; <span className="lightbox__ui-item-count-total">{slides && slides.length}</span>
          </li>
          <li onClick={() => { handleClickBack() }} className="lightbox__ui-listitem text--small">
            {'Back'}
          </li>
        </ul>
      </div>
    )
  }

  render() {
    const { 
      vw,
      vh,
      active,
      slides,
      currentIndex,
      mediaType
    } = this.props 

    const mediaClassName = `lightbox__wrapper--${mediaType}`

    const lightboxClassNames = classnames({
      'lightbox__wrapper--active': active,
      [mediaClassName]: Boolean(mediaType)
    }, 'lightbox__wrapper')

    return (
      <div className={lightboxClassNames} style={{ height: vh }}>
        <div className="lightbox__content">
          {slides && 
            <div 
              ref={el => this.carouselEl = el}
              className="lightbox__slide-list">
              {slides.map(slide => <LightboxSlide key={slide.index} active={slide.index === currentIndex - 1} vw={vw} vh={vh} slide={slide} />)}
            </div>
          }
          {this.renderUI()}
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Lightbox)


// WEBPACK FOOTER //
// ./src/components/lightbox-container/index.js